import "./styles/Modal.css";
import "./styles/Dashboard.css";
import { lazy, Suspense, useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import axios from "axios";
import "./styles/LoginPage.css";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { API_URL } from "./config";
import dayjs from "dayjs";
import "dayjs/locale/es";
import "utils/chartsSetup";
import { ThemeProvider } from "./contexts/ThemeContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { Box, CircularProgress } from "@mui/material";
import { usePermissionsStore } from "./stores/permissionsStore";
// import { UserProvider } from "./UserContext";
// import { esES } from "@mui/x-date-pickers/locales";

dayjs.locale("es"); // <- fuerza español para nombres de meses/días

export const axiosInstance = axios.create({
  baseURL: API_URL ?? "http://localhost:3001/api",
  withCredentials: true,
});

// Desactivar caching de GET requests
/*axiosInstance.interceptors.request.use((config) => {
  if (config.method === 'get') {
    config.headers['Cache-Control'] = 'no-cache';
    config.params = {
      ...config.params,
      t: new Date().getTime(), // Agregar timestamp para evitar caching
    };
  }
  return config;
});*/

const LoginPage = lazy(() => import("./pages/Login/Login"));
const Signup = lazy(() => import("./pages/Login/components/Signup"));
const ForgotPassword = lazy(() => import("./pages/Login/ForgotPassword"));
const ProtectedRoute = lazy(() => import("./Protected"));
const ProtectedLanding = lazy(() => import("./components/ProtectedLanding/ProtectedLanding"));
const MainLayout = lazy(() => import("./Layouts/MainLayout/pages/mainLayout"));
const UsersPage = lazy(() => import("./pages/Users/Users"));
const RolesPage = lazy(() => import("./pages/Users/Roles/Roles"));
const PurchasesPage = lazy(() => import("./pages/Purchases/Purchases"));
const ProvidersPage = lazy(() => import("./pages/Providers/Providers/Providers"));
const ProviderCategoriesPage = lazy(() => import("./pages/Providers/ProviderCategories/Categories"));
const InventoryCategoriesPage = lazy(() => import("./pages/Inventory/AssetCategories/Categories"));
const ReportsPage = lazy(() => import("./pages/Reports/Reports"));
// Dashboards temporalmente deshabilitados
// const DashboardsPage = lazy(() => import("./pages/Reports/Dashboards/Dashboards"));
// const DashboardDetailPage = lazy(() => import("./pages/Reports/Dashboards/DashboardDetail"));
const CostCenterPage = lazy(
  () => import("./pages/CostCenter/CostCenter/CostCenter")
);
const ChapterPage = lazy(() => import("./pages/CostCenter/Chapters/chapters"));
const Employees = lazy(() => import("./pages/Employees/employees/Employees"));
const PayrollSheets = lazy(
  () => import("./pages/Employees/payrolls/PayrollSheets")
);
const Overtimes = lazy(() => import("./pages/Employees/overtimes/Overtimes"));
const JobsPage = lazy(() => import("./pages/Employees/Jobs/Jobs"));
const InventoryPage = lazy(
  () => import("./pages/Inventory/Inventory/Inventory")
);
const AssetsPage = lazy(() => import("./pages/Inventory/Assets/Assets"));
const AssetDetailPage = lazy(() => import("./pages/Inventory/Assets/AssetDetail"));
const AssignmentsPage = lazy(() => import("./pages/Inventory/Assignments/Assignments"));
const ConsumablesPage = lazy(() => import("./pages/Inventory/Consumables/Consumables"));
const MaintenancesPage = lazy(() => import("./pages/Inventory/Maintenances/Maintenances"));
const InsurancesPage = lazy(() => import("./pages/Inventory/Insurances/Insurances"));
const KioskoPage = lazy(() => import("./pages/Kiosko/Kiosko"));
const KioskoAssignmentsPage = lazy(() => import("./pages/Kiosko/KioskoAssignments"));
const KioskoAssetDetailPage = lazy(() => import("./pages/Kiosko/KioskoAssetDetail"));
const Documentation = lazy(() => import("./pages/Documentation/Documentation"));

const LoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
      width: "100%",
    }}
  >
    <CircularProgress size={60} />
  </Box>
);

const App: React.FC = () => {
  const permissionsStore = usePermissionsStore();

  // Cargar permisos desde localStorage al iniciar la app
  useEffect(() => {
    permissionsStore.loadFromLocalStorage();
  }, []); // Array vacío: solo se ejecuta una vez al montar

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <ThemeProvider>
        <NotificationProvider>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<ProtectedLanding />} />
              <Route path="usuarios" element={<UsersPage />} />
              <Route path="usuarios/roles" element={<RolesPage />} />
              <Route path="compras" element={<PurchasesPage />} />
              <Route path="centro-costos" element={<CostCenterPage />} />
              <Route path="centro-costos/capitulos" element={<ChapterPage />} />
              <Route path="empleados" element={<Employees />} />
              <Route path="empleados/nominas" element={<PayrollSheets />} />
              <Route path="empleados/horas-extras" element={<Overtimes />} />
              <Route path="empleados/cargos" element={<JobsPage />} />
              <Route path="inventarios" element={<InventoryPage />} />
              <Route path="inventarios/categorias" element={<InventoryCategoriesPage />} />
              <Route path="inventarios/activos" element={<AssetsPage />} />
              <Route path="inventarios/activos/:assetId" element={<AssetDetailPage />} />
              <Route path="inventarios/asignaciones" element={<AssignmentsPage />} />
              <Route path="inventarios/mantenimientos" element={<MaintenancesPage />} />
              <Route path="inventarios/seguros" element={<InsurancesPage />} />
              <Route path="inventarios/consumibles" element={<ConsumablesPage />} />
              <Route path="proveedores" element={<ProvidersPage />} />
              <Route path="proveedores/categorias" element={<ProviderCategoriesPage />} />
              <Route path="reportes" element={<ReportsPage />} />
              <Route path="documentation" element={<Documentation />} />
              <Route path="kiosko" element={<KioskoPage />} />
              <Route path="kiosko/asignacion-activos" element={<KioskoAssignmentsPage />} />
              <Route path="kiosko/activos/:assetId" element={<KioskoAssetDetailPage />} />
              {/** Rutas de dashboards deshabilitadas temporalmente */}
              {/** <Route path="reportes/dashboards" element={<DashboardsPage />} /> */}
              {/** <Route path="reportes/dashboards/:id" element={<DashboardDetailPage />} /> */}
            </Route>
          </Routes>
        </Suspense>
        </NotificationProvider>
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default App;
